import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye, EyeOff, User, Lock, AlertCircle, ArrowLeft, LogIn } from "lucide-react";
import { BioButton } from "@/components/bio/BioButton";
import { StoreBadges } from "@/components/StoreBadges";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { LanguageToggle } from "@/components/LanguageToggle";
import { api, formatApiErrorDetail } from "@/lib/api";

const BIOFOOD_LOGO = "/biofood-blue.png";
const BIOFOOD_LOGO_WHITE = "/biofood-white.png";

function Aside() {
    return (
        <div className="hidden lg:flex flex-col justify-between bg-bio-hero text-white p-12 w-[42%] relative overflow-hidden">
            <img src={BIOFOOD_LOGO_WHITE} alt="BIOFOOD" className="h-7 w-auto self-start opacity-95" />
            <div>
                <h1 className="text-4xl font-extrabold leading-tight tracking-tight">
                    Gestión inteligente<br />de <span className="text-bio-accent-light">cafeterías escolares</span>
                </h1>
                <p className="mt-5 text-white/75 leading-relaxed max-w-sm">
                    Tecnología biométrica, POS, autoservicio y dispensadoras integradas en una sola plataforma.
                </p>
            </div>
            <p className="text-xs text-white/50 leading-relaxed">© 2026 RapidPay. Todos los derechos reservados.<br />RapidPay es un Payment Facilitator (PayFac).</p>
        </div>
    );
}

export default function Login() {
    const { user, login } = useAuth();
    const { t } = useLang();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [show, setShow] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [mode, setMode] = useState("login"); // login | forgot
    const [schools, setSchools] = useState([]);
    const [sede, setSede] = useState("");
    const [forgotSent, setForgotSent] = useState(false);

    useEffect(() => {
        api.get("/schools").then(({ data }) => setSchools(data)).catch(() => {});
    }, []);

    useEffect(() => {
        if (user && user.role) navigate(user.role === "parent" ? "/portal" : "/panel", { replace: true });
    }, [user, navigate]);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            const u = await login(email.trim(), password);
            navigate(u.role === "parent" ? "/portal" : "/panel", { replace: true });
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    const forgot = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            await api.post("/auth/forgot-password", { email: email.trim() });
            setForgotSent(true);
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    const campusOptions = schools.flatMap((s) =>
        (s.campuses || []).map((c) => ({ value: c.id, label: `${s.name} · ${c.name}` })),
    );

    return (
        <div className="min-h-screen flex bg-bio-bg font-poppins">
            <Aside />
            <div className="flex-1 flex items-center justify-center p-5">
                <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                    className="w-full max-w-md"
                >
                    <div className="lg:hidden flex justify-center mb-6">
                        <img src={BIOFOOD_LOGO} alt="BIOFOOD" className="h-9 w-auto" />
                    </div>

                    <div className="bg-white rounded-bio-lg shadow-bio-modal border border-bio-border-subtle p-7 md:p-8">
                        <div className="flex justify-end mb-2"><LanguageToggle /></div>
                        {mode === "login" ? (
                            <>
                                <h2 className="text-2xl font-bold text-bio-text">{t("login.welcome")}</h2>
                                <p className="text-sm text-bio-text-secondary mt-1">{t("login.subtitle")}</p>

                                <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
                                    {campusOptions.length > 1 && (
                                        <div className="space-y-1.5">
                                            <Label className="text-sm font-semibold">{t("login.campus")}</Label>
                                            <Select value={sede} onValueChange={setSede}>
                                                <SelectTrigger data-testid="login-campus" className="h-12 rounded-bio bg-bio-bg border-[1.5px]">
                                                    <SelectValue placeholder={t("login.campusPlaceholder")} />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {campusOptions.map((c) => (
                                                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    )}
                                    <div className="space-y-1.5">
                                        <Label className="text-sm font-semibold">{t("login.user")}</Label>
                                        <div className="relative">
                                            <User className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-bio-text-muted" />
                                            <Input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="correo@colegio.edu.pa" className="h-12 pl-10 rounded-bio bg-bio-bg border-[1.5px]" />
                                        </div>
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label className="text-sm font-semibold">{t("login.password")}</Label>
                                        <div className="relative">
                                            <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-bio-text-muted" />
                                            <Input data-testid="login-password" type={show ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Contraseña" className="h-12 pl-10 pr-10 rounded-bio bg-bio-bg border-[1.5px]" />
                                            <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-bio-text-muted hover:text-bio-text" tabIndex={-1} aria-label="Mostrar contraseña">
                                                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                            </button>
                                        </div>
                                    </div>

                                    {error && (
                                        <div data-testid="login-error" className="flex items-center gap-2 text-sm text-bio-error bg-[#FDECEA] rounded-bio px-3 py-2.5">
                                            <AlertCircle className="h-4 w-4 shrink-0" /> {error}
                                        </div>
                                    )}

                                    <div className="flex justify-end">
                                        <button type="button" onClick={() => { setMode("forgot"); setError(""); }} className="text-sm font-semibold text-bio-primary hover:underline">
                                            ¿Olvidaste tu contraseña?
                                        </button>
                                    </div>

                                    <BioButton type="submit" loading={loading} data-testid="login-submit" className="w-full h-[54px] text-base shadow-[0_14px_30px_-10px_rgba(0,80,138,0.6)] hover:shadow-[0_18px_38px_-10px_rgba(0,80,138,0.7)]">
                                        {!loading && <LogIn className="h-5 w-5" />}
                                        {t("login.submit")}
                                    </BioButton>
                                </form>
                            </>
                        ) : (
                            <>
                                <button onClick={() => { setMode("login"); setForgotSent(false); setError(""); }} className="flex items-center gap-1.5 text-sm font-semibold text-bio-primary mb-4">
                                    <ArrowLeft className="h-4 w-4" /> Volver
                                </button>
                                {forgotSent ? (
                                    <div data-testid="forgot-sent">
                                        <h2 className="text-2xl font-bold text-bio-text">Revisa tu correo</h2>
                                        <p className="text-sm text-bio-text-secondary mt-2">Si el correo existe, enviamos un enlace para restablecer tu contraseña. (En MODO DEMO el enlace se registra en el log del servidor.)</p>
                                        <BioButton className="w-full mt-6" onClick={() => { setMode("login"); setForgotSent(false); }}>Entendido</BioButton>
                                    </div>
                                ) : (
                                    <>
                                        <h2 className="text-2xl font-bold text-bio-text">¿Olvidaste tu contraseña?</h2>
                                        <p className="text-sm text-bio-text-secondary mt-1">Ingresa tu correo para restablecerla.</p>
                                        <form onSubmit={forgot} className="mt-6 space-y-4" data-testid="forgot-form">
                                            <div className="space-y-1.5">
                                                <Label className="text-sm font-semibold">Correo</Label>
                                                <Input data-testid="forgot-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="correo@colegio.edu.pa" className="h-12 rounded-bio bg-bio-bg border-[1.5px]" />
                                            </div>
                                            {error && <div className="flex items-center gap-2 text-sm text-bio-error bg-[#FDECEA] rounded-bio px-3 py-2.5"><AlertCircle className="h-4 w-4" /> {error}</div>}
                                            <BioButton type="submit" loading={loading} data-testid="forgot-submit" className="w-full">Enviar enlace</BioButton>
                                        </form>
                                    </>
                                )}
                            </>
                        )}
                    </div>
                    <div className="mt-6 pt-5 border-t border-bio-border-subtle text-center">
                        <p className="text-xs font-semibold uppercase tracking-wider text-bio-text-muted mb-3">Descarga la app</p>
                        <StoreBadges className="justify-center" />
                    </div>
                    <p className="text-center text-xs text-bio-text-muted mt-5">
                        <Link to="/" className="hover:text-bio-primary">← Volver al inicio</Link>
                    </p>
                    <p className="text-center text-[11px] text-bio-text-muted mt-3 leading-relaxed px-4">
                        © 2026 RapidPay. Todos los derechos reservados. RapidPay es un Payment Facilitator (PayFac).
                    </p>
                </motion.div>
            </div>
        </div>
    );
}
